import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  ProductPod,
  PodFooter,
  ProductFuelType,
  PodHeader,
  PodSection,
  PodSpacer,
  PodRowToColumn,
  PodRow,
  PodTapper,
  ProductCompare,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductImage,
  ProductCustomOptions,
  BundleDataModel
} from '@thd-olt-component-react/product-pod';
import { AddTo } from '@thd-olt-component-react/add-to';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { ProductSeeSimilarItems, ViewSimilarIcon } from '@thd-olt-component-react/product-pod-group';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { Promotion } from '@thd-olt-component-react/promotion';
import { sendPodTapperAnalytics } from '@thd-olt-component-react/product-pod/dist/components/productPodUtils';
import { useProductPodClickTracker } from '../../hooks/useProductPodClickTracker';

export const handleResponsiveSuperSkuMiniSwatchChange = ({
  superSkuChangedItemIdsRef,
  clickData,
  onChange
}) => {
  if (!superSkuChangedItemIdsRef.current.includes(clickData.itemId)) {
    superSkuChangedItemIdsRef.current = [
      ...superSkuChangedItemIdsRef.current,
      clickData.itemId
    ];
  }

  onChange(clickData);
};

export const createResponsiveSuperSkuMiniSwatchOnChangeHandler = ({
  superSkuChangedItemIdsRef,
  onChange
}) => (clickData) => handleResponsiveSuperSkuMiniSwatchChange({
  superSkuChangedItemIdsRef,
  clickData,
  onChange
});

export const CustomProductPodResponsive = ({
  itemId,
  parentId,
  storeId,
  position,
  onCompare,
  onSelectedForCompare,
  selectedForCompare,
  hasInStoreFilter,
  parent,
  impProductPodData,
  showProjectQuantity,
  hasSameDayFilter,
  url,
  isMobileOrConsumerApp,
  isBundleProduct,
  isFulfillmentFilterAndMsgEnabled,
  isViewSimilarButtonEnabled
}) => {
  const theme = useTheme(CustomProductPodResponsive);
  const { hideAddToListIcon, hideAddToListButton } = theme.props;
  const superSkuChangedItemIdsRef = useRef([]);
  const disableAddTo = useConfigService('fs:disableAddTo');
  const showFulfillmentPodStore = isFulfillmentFilterAndMsgEnabled ? !hasSameDayFilter : true;
  const showFulfillmentPodShipping = isFulfillmentFilterAndMsgEnabled ? !hasInStoreFilter : true;

  const productPodNodeRef = useProductPodClickTracker({ itemId, position });

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      className="product-pod sui-h-full"
      data-testid="product-pod"
    >
      <ProductPod
        analyticsData={{ parent, position }}
        analyticImpressionData={impProductPodData}
        hasSSKU
        itemId={itemId}
        storeId={storeId}
        padding="sui-p-2 sm:sui-pt-2 sm:sui-px-4 sm:sui-pb-4"
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-10"
              href={url}
              aria-label="Link"
            >
              <span className="sui-sr-only">View product details</span>
            </a>
            <div className="sui-flex sui-flex-col sui-h-full">
              <PodSpacer padding={['BOTTOM']} minHeight="32px">
                <PodHeader noPadding id="Slot-1-badges-and-heart">
                  <PodRow noPadding>
                    <ProductBadge itemId={pod.itemId} storeId={storeId} />
                    {isMobileOrConsumerApp
                      && <ProductSponsored itemId={pod.itemId} position={position} />}
                  </PodRow>
                  {!hideAddToListIcon && (
                    <PodTapper fitWidth zIndex>
                      <PodRow rowReverse noPadding>
                        <AddToList itemId={pod.itemId} showIconButton />
                      </PodRow>
                    </PodTapper>
                  )}
                </PodHeader>
              </PodSpacer>
              <PodSection
                id="Slot-2"
                display="sui-grid"
                gap="sui-gap-0"
                noPadding
              >
                <PodRowToColumn
                  noPadding
                  gap="sui-gap-x-4 md:sui-gap-x-0"
                  id="Slot-2A-image-details-and-ratings"
                >
                  <PodTapper zIndex>
                    <ProductImage
                      itemId={pod.itemId}
                      showSecondaryImage={pod.showSecondaryImage}
                      dualImageSwipe={isMobileOrConsumerApp}
                    />
                  </PodTapper>
                  <PodSection
                    display="sui-block sui-w-full"
                    noPadding
                    padding="sui-pt-2"
                    grid="sui-col-start-1 sui-col-end-3 md:sui-order-2"
                    order="sui-order-3"
                  >
                    <PodTapper
                      zIndex
                      aPointer
                      buttonPointer
                      cursorPointer
                      handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                    >
                      <PodSpacer>
                        <PodRow noPadding>
                          <div className="sui-h-6">
                            {!isMobileOrConsumerApp && (
                              <ProductSponsored itemId={pod.itemId} position={position} noPadding noBackground />
                            )}
                          </div>
                        </PodRow>
                      </PodSpacer>
                      <ProductSample itemId={pod.itemId} />
                      <SuperSkuMiniSwatches
                        storeId={storeId}
                        itemId={pod.itemId}
                        onChange={createResponsiveSuperSkuMiniSwatchOnChangeHandler({
                          superSkuChangedItemIdsRef,
                          onChange: pod.onChange
                        })}
                        onHover={pod.onSSKUHover}
                        componentImpressionData={{
                          name: 'ProductPod',
                          component: 'ProductPod',
                          position: position + 1,
                          type: 'product',
                          anchorSku: parentId
                        }}
                        isImpressionsRequired
                        hideComponentWhenEmpty
                      />
                      <ProductServiceAddOns itemId={pod.itemId} />
                    </PodTapper>
                  </PodSection>
                  {isViewSimilarButtonEnabled?.isViewSimilarTopButtonEnabled && (
                    <PodSection
                      display="sui-block sui-w-full"
                      noPadding
                      grid="sui-col-start-1 sui-col-end-3 md:sui-order-2"
                      order="sui-order-3"
                      columnAlign
                      zIndex
                      fitWidth
                    >
                      <div className="sui-flex sui-items-center sui-z-20">
                        <ViewSimilarIcon
                          itemId={pod.itemId}
                          products={isViewSimilarButtonEnabled?.productArray}
                          storeId={storeId}
                        />
                      </div>
                    </PodSection>
                  )}
                  <PodSection
                    display="sui-grid"
                    gap="sui-gap-0"
                    order="sui-order-2 md:sui-order-3"
                    padding="sui-mb-auto sui-px-0"
                    alignTop
                    noPadding
                  >
                    <PodTapper fitWidth zIndex>
                      <PodSpacer margin="md:sui-mt-1">
                        <ProductHeader
                          brand="inline"
                          brandLineHeight="tight"
                          brandFontSize="sm"
                          productNameLineHeight="tight"
                          itemId={pod.itemId}
                          brandTitleMaxLine={4}
                          titleMaxLine={!isMobileOrConsumerApp ? 2 : 3}
                          disableShopThisCollection
                        />
                      </PodSpacer>
                    </PodTapper>
                    <PodTapper fitWidth zIndex>
                      <ProductRating itemId={pod.itemId} />
                    </PodTapper>
                    <PodSpacer fitWidth zIndex>
                      <ProductIdentifier itemId={pod.itemId} type="model" />
                    </PodSpacer>
                    <PodTapper
                      fitWidth
                      zIndex
                      handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                    >
                      <ProductFuelType itemId={pod.itemId} />
                    </PodTapper>
                  </PodSection>
                </PodRowToColumn>
                <PodSection noPadding alignTop columnAlign padding="sui-px-0">
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer
                      padding={['TOP', 'BOTTOM']}
                      disable={pod.productSubType === 'HDQC'}
                    >
                      <Price
                        clsRemediation={{ placeholders: true, preservePlaceholders: true }}
                        disableOuterSpacing
                        itemId={pod.itemId}
                        large={false}
                        storeId={storeId}
                        displayEachUom={false}
                        onSavingsCenterToggle={pod.onSavingsCenterToggle}
                        hideForProductTypes={['HDQC']}
                        showPreferredPricingBadge
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
                <PodSection noPadding alignTop columnAlign padding="sui-px-0">
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer
                      padding={['TOP', 'BOTTOM']}
                    >
                      <Promotion
                        itemId={pod.itemId}
                        hideConditionalPromoDescription
                        pageType={parent.toUpperCase()}
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
              </PodSection>
              <PodFooter noPadding margin="sui-mt-auto">
                <PodSection display="sui-grid" padding="sui-px-0" noPadding alignEnd>
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    {
                      showFulfillmentPodStore
                      && (
                        <PodSpacer padding={['TOP', 'BOTTOM']} hideForProductTypes={['HDQC']}>
                          <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} />
                        </PodSpacer>
                      )
                    }
                    {
                      showFulfillmentPodShipping
                      && (
                        <PodSpacer
                          padding={['B2']}
                          disable={pod.productSubType === 'HDQC' || pod.productSubType === 'SFI'}
                        >
                          <FulfillmentPodShipping
                            itemId={pod.itemId}
                            storeId={pod.storeId}
                            hasSameDayFilter={hasSameDayFilter}
                            showFSDBMMessage
                            showFasterPodSpacing
                            isAppliancePod
                          />
                        </PodSpacer>
                      )
                    }
                  </PodTapper>
                </PodSection>
                <PodSection display="sui-grid" gap="sui-gap-0" noPadding alignEnd>
                  <PodTapper zIndex>
                    <PodRow noPadding>
                      <div className="sui-flex-1">
                        <ProductATC
                          variant={!hideAddToListButton ? 'primary' : 'secondary'}
                          hasInStoreFilter={hasInStoreFilter}
                          showProjectQuantity={showProjectQuantity}
                          storeId={storeId}
                          itemId={pod.itemId}
                          checkGeneric
                          outline
                        />
                        <PodSpacer disable={pod.productSubType !== 'HDQC'}>
                          <NonBuyableQuoteCenter
                            itemId={pod.itemId}
                            checkForProductSubType={['HDQC']}
                          />
                        </PodSpacer>
                      </div>
                      {isViewSimilarButtonEnabled?.isViewSimilarBottomButtonEnabled && (
                        <div className="sui-flex sui-items-center sui-pl-2">
                          <ViewSimilarIcon
                            itemId={pod.itemId}
                            products={isViewSimilarButtonEnabled?.productArray}
                            storeId={storeId}
                          />
                        </div>
                      )}
                    </PodRow>
                  </PodTapper>
                  {!hideAddToListButton && (
                    <PodTapper zIndex>
                      <PodSpacer padding={['TOP']}>
                        {
                          disableAddTo ? (
                            <AddToList itemId={pod.itemId} storeId={storeId} />
                          ) : (
                            <AddTo
                              products={[{
                                itemId: pod.itemId,
                                quantity: 1
                              }]}
                              storeId={storeId}
                              addToType="Project"
                              addToFallbackType="List"
                            />
                          )
                        }
                      </PodSpacer>
                    </PodTapper>
                  )}
                  {!isMobileOrConsumerApp && (
                    <ProductCompare
                      itemId={pod.itemId}
                      onCompare={onCompare}
                      onSelectedForCompare={onSelectedForCompare}
                      selectedForCompare={selectedForCompare}
                      disableCompare={isBundleProduct}
                      tap
                    />
                  )}
                </PodSection>
              </PodFooter>
            </div>
          </>
        )}
      />
    </div>
  );
};

CustomProductPodResponsive.displayName = 'CustomProductPodResponsive';

CustomProductPodResponsive.themeProps = {
  hideAddToListIcon: PropTypes.bool,
  hideAddToListButton: PropTypes.bool
};

CustomProductPodResponsive.defaultThemeProps = {
  hideAddToListIcon: false,
  hideAddToListButton: true
};

CustomProductPodResponsive.propTypes = {
  itemId: PropTypes.string.isRequired,
  parentId: PropTypes.string,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  hasInStoreFilter: PropTypes.bool.isRequired,
  url: PropTypes.string.isRequired,
  onSelectedForCompare: PropTypes.func,
  onCompare: PropTypes.func,
  selectedForCompare: PropTypes.arrayOf(PropTypes.string),
  impProductPodData: PropTypes.shape({}),
  parent: PropTypes.string,
  showProjectQuantity: PropTypes.bool,
  hasSameDayFilter: PropTypes.bool,
  isMobileOrConsumerApp: PropTypes.bool,
  isBundleProduct: PropTypes.bool,
  isFulfillmentFilterAndMsgEnabled: PropTypes.bool,
  isViewSimilarButtonEnabled: PropTypes.shape({
    isViewSimilarTopButtonEnabled: PropTypes.bool,
    isViewSimilarBottomButtonEnabled: PropTypes.bool,
    productArray: PropTypes.arrayOf(PropTypes.shape({}))
  })
};

CustomProductPodResponsive.defaultProps = {
  impProductPodData: null,
  parent: 'plp',
  showProjectQuantity: false,
  hasSameDayFilter: false,
  isMobileOrConsumerApp: false,
  onSelectedForCompare: () => { },
  onCompare: () => { },
  selectedForCompare: [],
  isBundleProduct: false,
  isFulfillmentFilterAndMsgEnabled: false,
  parentId: null,
  isViewSimilarButtonEnabled: {
    isViewSimilarTopButtonEnabled: false,
    isViewSimilarBottomButtonEnabled: false,
    productArray: []
  }
};

CustomProductPodResponsive.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductServiceAddOns,
  AddToList,
  AddTo,
  Promotion,
  Price,
  PriceClearanceDataModel,
  SuperSkuMiniSwatches,
  ProductATC,
  ProductRating,
  ProductCompare,
  ProductFuelType,
  ProductSample,
  ProductHeader,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  ProductCustomOptions,
  KeyProductFeatures,
  ProductSeeSimilarItems,
  NonBuyableQuoteCenter,
  BundleDataModel,
  ViewSimilarIcon
);
